/**
 * <frs-product> : compteur de galerie (mobile), barre d'achat sticky (mobile), ajout au panier en AJAX.
 * L'ajout émet l'événement standard CartLinesUpdateEvent : la pastille du header et le tiroir FRS se mettent à jour.
 * Sans JS, le formulaire poste normalement vers /cart/add.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

class FrsProduct extends HTMLElement {
  connectedCallback() {
    this.setupGallery();
    this.setupStickyBar();
    this.form = this.querySelector('[data-frs-form]');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  setupGallery() {
    const gallery = this.querySelector('[data-gallery]');
    const count = this.querySelector('[data-gallery-count]');
    if (!gallery || !count) return;
    const total = gallery.children.length;
    gallery.addEventListener(
      'scroll',
      () => {
        const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
        count.textContent = `${Math.min(index + 1, total)} / ${total}`;
      },
      { passive: true }
    );
  }

  setupStickyBar() {
    const bar = this.querySelector('[data-sticky-bar]');
    const button = this.querySelector('[data-add]');
    if (!bar || !button || !('IntersectionObserver' in window)) return;
    const mobile = matchMedia('(max-width: 860px)');
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0 && mobile.matches;
      bar.classList.toggle('is-on', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
    });
    this.observer.observe(button);
  }

  async onSubmit(event) {
    event.preventDefault();
    const buttons = [this.querySelector('[data-add]'), this.querySelector('[data-sticky-bar] button')].filter(Boolean);
    const message = this.querySelector('[data-message]');
    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    buttons.forEach((b) => b.setAttribute('aria-disabled', 'true'));
    if (message) message.textContent = '';

    const sectionId = document.querySelector('frs-cart-drawer')?.dataset.sectionId;
    if (sectionId) formData.append('sections', sectionId);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: Number(formData.get('quantity') || 1) }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'add failed');

      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, didError: false },
      });
      document.querySelector('frs-cart-drawer')?.open(data.sections);
    } catch (error) {
      deferred.reject(error);
      if (message) message.textContent = error.message && error.message !== 'add failed' ? error.message : this.dataset.error || 'Erreur';
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-disabled'));
    }
  }
}

if (!customElements.get('frs-product')) customElements.define('frs-product', FrsProduct);
